import React, { useState, useEffect } from 'react';
import headerLogo from 'images/superb-logo-black.svg';
import PredictionsIcon from 'images/icons/PredictionsIcon';
import PredictionsEngineIcon from 'images/icons/PredictionsEngineIcon';
import { routes } from 'routes/routes';
import { logOut, getFromLocalStorage } from 'services/localstorage';
import SearchField from 'components/searchField/SearchField';
import constants from 'utils/constants';
import { useLocation } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import { useFlag } from '@unleash/proxy-client-react';
import { FilePlus2 } from 'lucide-react';
import { useUser } from 'hooks/useUser';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import SIDEBAR_ITEMS from '../../pages/film-details/SidebarItems';
import NotificationsPopup from './notifications/NotificationsPopup';
import SlateReportModal from '../modals/SlateReportModal';
import AccountLimitsExceededTooltip from 'components/tooltips/AccountLimitsExceededTooltip';
import './PrivateHeader.scss';
import { PERMISSIONS } from 'types/user';
import { useHasPermissions } from 'hooks/useUser';
import { useStatistics } from 'queries/account';

interface PrivateHeaderProps {
  displaySearchFieldComponent?: boolean;
  providePublicBrandInfoOnly?: boolean;
}

type IconLinkName =
  | 'addFilm'
  | 'predictions'
  | 'predictionsEngine'
  | 'library'
  | 'distributors'
  | 'notifications'
  | 'profile';

type IconLinksActiveState = Record<IconLinkName, boolean>;

const PrivateHeader: React.FC<PrivateHeaderProps> = ({
  displaySearchFieldComponent,
  providePublicBrandInfoOnly,
}) => {
  const { user, updateData } = useUser();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [isSlateReportModalOpen, setIsSlateReportModalOpen] = useState(false);
  const hasPredictionsEngineAccess = useHasPermissions([
    PERMISSIONS.CAN_ACCESS_PREDICTIONS_ENGINE,
  ]);
  const { data: statistics } = useStatistics();

  const toggleDropdown = () => {
    setIsOpenDropdown(!isOpenDropdown);
    handleIconLinkClick(privateHeaderIconLinks.profile as IconLinkName);
  };

  // Mobile-first: on phones the icons sit next to the logo and the search
  // field wraps onto its own full-width row below; md: restores the original
  // single row (search filling the middle, icons on the right).
  const privateIconsAndSearchFieldComponentWrapperClassName = cn(
    'tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-flex-1 tw-min-w-0',
    'md:tw-flex-nowrap md:tw-gap-4',
    displaySearchFieldComponent ? 'md:tw-justify-start' : 'md:tw-justify-end',
    providePublicBrandInfoOnly && 'tw-hidden',
  );

  const location = useLocation();
  const query = useQuery();
  const isNotificationsFeatureActive = useFlag(
    constants.FEATURE_FLAG.notifications,
  );
  const isDashboardFeatureActive = useFlag(constants.FEATURE_FLAG.dashboard);

  const userName = getFromLocalStorage('username');

  const { privateHeaderIconLinks } = constants;

  const defaultIconLinksActiveState: IconLinksActiveState = {
    addFilm: false,
    predictions: false,
    predictionsEngine: false,
    library: false,
    distributors: false,
    notifications: false,
    profile: false,
  };

  const [iconLinksActiveState, setIconLinksActiveState] =
    useState<IconLinksActiveState>(defaultIconLinksActiveState);

  const handleIconLinkClick = (iconLinkName: IconLinkName) => {
    if (
      iconLinkName === 'addFilm' ||
      iconLinkName === 'predictions' ||
      iconLinkName === 'predictionsEngine' ||
      iconLinkName === 'library' ||
      iconLinkName === 'distributors'
    ) {
      setIconLinksActiveState({
        ...defaultIconLinksActiveState,
        [iconLinkName]: true,
      });
    } else {
      const updatedIconLinksActiveState = { ...iconLinksActiveState };

      if (iconLinkName === 'notifications') {
        updatedIconLinksActiveState.notifications =
          !iconLinksActiveState.notifications;

        updatedIconLinksActiveState.profile = false;
      } else if (iconLinkName === 'profile') {
        updatedIconLinksActiveState.profile = !iconLinksActiveState.profile;

        updatedIconLinksActiveState.notifications = false;
      }

      setIconLinksActiveState(updatedIconLinksActiveState);
    }
  };

  const handleLocationChange = () => {
    if (
      location.pathname.includes(routes.FILM_DETAILS) &&
      location.search.includes(
        `subpage=${SIDEBAR_ITEMS.Overview.short}&mode=edit`,
      ) &&
      !query.get('id')
    ) {
      setIconLinksActiveState({
        ...defaultIconLinksActiveState,
        addFilm: true,
      });
    } else if (location.pathname.includes(routes.PREDICTIONS)) {
      setIconLinksActiveState({
        ...defaultIconLinksActiveState,
        predictions: true,
      });
    } else if (location.pathname.includes(routes.PREDICTIONS_ENGINE)) {
      setIconLinksActiveState({
        ...defaultIconLinksActiveState,
        predictionsEngine: true,
      });
    } else if (location.pathname.includes(routes.LIBRARY)) {
      setIconLinksActiveState({
        ...defaultIconLinksActiveState,
        library: true,
      });
    } else if (location.pathname.includes(routes.DISTRIBUTORS)) {
      setIconLinksActiveState({
        ...defaultIconLinksActiveState,
        distributors: true,
      });
    } else {
      setIconLinksActiveState({
        ...defaultIconLinksActiveState,
      });
    }
  };

  useEffect(() => {
    handleLocationChange();
  }, [location]);

  return (
    <div className="privateHeader tw-sticky tw-top-0 tw-z-[999]">
      <nav className="tw-flex tw-items-center tw-justify-between tw-bg-white tw-px-3 tw-py-2 md:tw-items-stretch md:tw-px-4 md:tw-py-2.5">
        {/* @ts-ignore */}
        <Link
          to={routes.ROOT}
          className="tw-shrink-0 tw-py-1 tw-mr-2 md:tw-py-1.5 md:tw-mr-4"
        >
          <img
            src={headerLogo}
            alt="Header Logo"
            className="tw-h-[28px] md:tw-h-[37px]"
          />
        </Link>

        <div className={privateIconsAndSearchFieldComponentWrapperClassName}>
          {displaySearchFieldComponent && (
            <div className="tw-order-last tw-basis-full tw-min-w-0 md:tw-order-none md:tw-flex-1">
              <SearchField isHeader={true} />
            </div>
          )}

          {/* On phones the icon strip may scroll sideways rather than push the page wider. */}
          <div className="tw-flex tw-items-center tw-gap-0 tw-min-w-0 tw-overflow-x-auto [&::-webkit-scrollbar]:tw-hidden md:tw-gap-1 md:tw-flex-shrink-0 md:tw-overflow-visible">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="tw-flex tw-items-center">
                  {statistics?.can_create_movie ? (
                    <a
                      href={`/film-details?subpage=${SIDEBAR_ITEMS.Overview.short}&mode=edit`}
                      id="privateHeaderAddFilmIcon"
                      className="tw-group tw-flex tw-px-2 tw-py-0.5 tw-text-base tw-cursor-pointer tw-no-underline hover:tw-no-underline tw-text-[#8a8a8a] hover:tw-text-black tw-transition-colors"
                    >
                      <span
                        className={`icon-film-add ${
                          iconLinksActiveState.addFilm ? 'activeIconLink' : ''
                        }`}
                      />
                    </a>
                  ) : (
                    <div
                      className="tw-flex tw-px-2 tw-py-0.5 tw-text-base tw-cursor-not-allowed tw-opacity-50"
                      style={{ color: '#8a8a8a' }}
                      onClick={(e) => e.preventDefault()}
                    >
                      <span className="icon-film-add" />
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="tw-bg-gray-900 tw-text-white tw-text-xs tw-max-w-[250px]"
              >
                {statistics?.can_create_movie ? (
                  'Add a film'
                ) : (
                  <AccountLimitsExceededTooltip />
                )}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="tw-flex tw-items-center">
                  {/* @ts-ignore */}
                  <Link
                    to={routes.PREDICTIONS}
                    id="privateHeaderPredictionsIcon"
                    className="tw-flex tw-px-2 tw-py-0.5 tw-text-base tw-cursor-pointer tw-no-underline hover:tw-no-underline"
                  >
                    <PredictionsIcon
                      color={
                        iconLinksActiveState.predictions ? '#191919' : '#8a8a8a'
                      }
                      size={16}
                    />
                  </Link>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="tw-bg-gray-900 tw-text-white tw-text-xs"
              >
                Predictions
              </TooltipContent>
            </Tooltip>

            {hasPredictionsEngineAccess &&
              user?.profile?.is_predictions_engine_enabled && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="tw-flex tw-items-center">
                      {statistics?.can_create_prediction ? (
                        // @ts-ignore
                        <Link
                          to={routes.PREDICTIONS_ENGINE}
                          id="privateHeaderPredictionsEngineIcon"
                          className="tw-flex tw-px-2 tw-py-0.5 tw-text-base tw-cursor-pointer tw-no-underline hover:tw-no-underline"
                        >
                          <PredictionsEngineIcon
                            color={
                              iconLinksActiveState.predictionsEngine
                                ? '#191919'
                                : '#8a8a8a'
                            }
                            size={16}
                          />
                        </Link>
                      ) : (
                        <div
                          className="tw-flex tw-px-2 tw-py-0.5 tw-text-base tw-cursor-not-allowed tw-opacity-50"
                          onClick={(e) => e.preventDefault()}
                        >
                          <PredictionsEngineIcon color="#8a8a8a" size={16} />
                        </div>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="tw-bg-gray-900 tw-text-white tw-text-xs tw-max-w-[250px]"
                  >
                    {statistics?.can_create_prediction ? (
                      'Predictions Engine'
                    ) : (
                      <AccountLimitsExceededTooltip />
                    )}
                  </TooltipContent>
                </Tooltip>
              )}

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="tw-flex tw-items-center">
                  <button
                    onClick={() => setIsSlateReportModalOpen(true)}
                    className="tw-bg-transparent tw-border-0 tw-p-2 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-min-w-[32px] tw-min-h-[32px]"
                  >
                    <FilePlus2
                      size={16}
                      color="#8a8a8a"
                      className="tw-transition-colors hover:tw-text-gray-700"
                    />
                  </button>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="tw-bg-gray-900 tw-text-white tw-text-xs"
              >
                Slate Report
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="tw-flex tw-items-center">
                  {/* @ts-ignore */}
                  <Link
                    to={routes.DISTRIBUTORS}
                    id="privateHeaderDistributorsIcon"
                    className="tw-group tw-flex tw-px-2 tw-py-0.5 tw-text-base tw-cursor-pointer tw-no-underline hover:tw-no-underline tw-text-[#8a8a8a] hover:tw-text-black tw-transition-colors"
                  >
                    <span
                      className={`icon-globe ${
                        iconLinksActiveState.distributors
                          ? 'activeIconLink'
                          : ''
                      }`}
                    />
                  </Link>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="tw-bg-gray-900 tw-text-white tw-text-xs"
              >
                Distributors
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="tw-flex tw-items-center">
                  {/* @ts-ignore */}
                  <Link
                    to={routes.LIBRARY}
                    id="privateHeaderLibraryIcon"
                    className="tw-group tw-flex tw-px-2 tw-py-0.5 tw-text-base tw-cursor-pointer tw-no-underline hover:tw-no-underline tw-text-[#8a8a8a] hover:tw-text-black tw-transition-colors"
                  >
                    <span
                      className={`icon-book ${
                        iconLinksActiveState.library ? 'activeIconLink' : ''
                      }`}
                    />
                  </Link>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="tw-bg-gray-900 tw-text-white tw-text-xs"
              >
                Library
              </TooltipContent>
            </Tooltip>

            {isNotificationsFeatureActive && (
              <NotificationsPopup
                handleNotificationsIconLinkClick={() =>
                  handleIconLinkClick('notifications')
                }
              />
            )}

            <DropdownMenu
              open={isOpenDropdown}
              onOpenChange={setIsOpenDropdown}
            >
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    'tw-rounded-full tw-bg-gray-100 tw-border-0 tw-w-10 tw-h-10 tw-p-2 tw-cursor-pointer',
                    'tw-flex tw-items-center tw-justify-center tw-outline-none',
                    'hover:tw-bg-gray-200 focus:tw-bg-gray-200',
                    'tw-transition-colors tw-duration-150',
                    'focus-visible:tw-ring-2 focus-visible:tw-ring-gray-400 focus-visible:tw-ring-offset-2',
                    iconLinksActiveState.profile && 'tw-bg-gray-200',
                  )}
                  onClick={() => handleIconLinkClick('profile')}
                  aria-label="User menu"
                >
                  <span className="icon-user tw-text-gray-600" />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                className="tw-w-52 tw-shadow-lg tw-border tw-border-gray-200 tw-z-[1000]"
              >
                <DropdownMenuLabel className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-px-3 tw-py-2">
                  {userName}
                </DropdownMenuLabel>

                <DropdownMenuSeparator className="tw-my-1" />

                <DropdownMenuItem asChild>
                  <a
                    href={routes.ACCOUNT}
                    className={cn(
                      'tw-flex tw-items-center tw-w-full tw-px-3 tw-py-2',
                      'tw-text-sm tw-text-gray-700 tw-no-underline',
                      'tw-transition-colors tw-duration-150',
                      'hover:tw-bg-gray-100 hover:tw-text-gray-900 hover:tw-no-underline',
                      'focus:tw-bg-gray-100 focus:tw-text-gray-900 focus:tw-outline-none',
                    )}
                  >
                    <span className="icon-user tw-mr-2 tw-text-gray-500 tw-text-sm" />
                    Account
                  </a>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <a
                    href={routes.CONTACT_US}
                    className={cn(
                      'tw-flex tw-items-center tw-w-full tw-px-3 tw-py-2',
                      'tw-text-sm tw-text-gray-700 tw-no-underline',
                      'tw-transition-colors tw-duration-150',
                      'hover:tw-bg-gray-100 hover:tw-text-gray-900 hover:tw-no-underline',
                      'focus:tw-bg-gray-100 focus:tw-text-gray-900 focus:tw-outline-none',
                    )}
                  >
                    <span className="icon-mail tw-mr-2 tw-text-gray-500 tw-text-sm" />
                    Contact
                  </a>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="tw-my-1" />

                <DropdownMenuItem asChild>
                  <a
                    href={routes.LOGIN}
                    className={cn(
                      'tw-flex tw-items-center tw-w-full tw-px-3 tw-py-2',
                      'tw-text-sm tw-text-red-600 tw-no-underline',
                      'tw-transition-colors tw-duration-150',
                      'hover:tw-bg-red-50 hover:tw-text-red-700 hover:tw-no-underline',
                      'focus:tw-bg-red-50 focus:tw-text-red-700 focus:tw-outline-none',
                    )}
                    onClick={() => {
                      logOut();
                      updateData({ active: false });
                    }}
                  >
                    <span className="icon-log-out tw-mr-2 tw-text-red-500 tw-text-sm" />
                    Log Out
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {isSlateReportModalOpen && (
        <SlateReportModal
          onOpenChange={() => setIsSlateReportModalOpen(false)}
        />
      )}
    </div>
  );
};

export default PrivateHeader;
